import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { CheckCircle } from "lucide-react";
import ShopifyConnectionView from "./ShopifyConnectionView";
import { useImportShopify } from "../hooks/useImportShopify";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { useNavigate } from "react-router-dom";
import ExcelImportView from "./ExcelImportView";
import { useTranslation } from "../i18n/hooks";


type GetStartedViewProps = {
  initialStep: number;
};

const GetStartedView = ({ initialStep }: GetStartedViewProps) => {
  const { t } = useTranslation();
  const { importShopify, isImportLoading } = useImportShopify();
  const navigate = useNavigate();
  const { shopName } = useSelector((state: RootState) => state.flow);
  const [step, setStep] = useState(initialStep);
  const [selectedImportMethod, setSelectedImportMethod] = useState<"shopify" | "excel" | null>(shopName ? "shopify" : null);
  const {currentCollection} = useSelector((state: RootState) => state.user);
  const steps = [
    {
      title: t('getStarted.import_data'),
      description: t('getStarted.import_data_desc'),
      content: (
        <div>
          <div>
            <div className="flex items-center gap-2 h-[60px]">
                <Button
                onClick={() => {
                  setSelectedImportMethod("shopify");
                }}
                className={`w-full h-full ${
                  selectedImportMethod !== "shopify" ? "bg-gray-100 hover:bg-gray-200" : ""
                }`}
                variant={selectedImportMethod !== "shopify" ? "secondary" : "default"}
                >
                {t('getStarted.shopify')}
                </Button>
                <Button
                onClick={() => {
                  setSelectedImportMethod("excel");
                }}
                data-testid="excel-option"
                className={`w-full h-full ${
                  selectedImportMethod !== "excel" ? "bg-gray-100 hover:bg-gray-200" : ""
                }`}
                variant={selectedImportMethod !== "excel" ? "secondary" : "default"}
                >
                {t('getStarted.excel')}
                </Button>
            </div>
          </div>
         {selectedImportMethod === "shopify" && (<ShopifyConnectionView
          reauth={() => {}}
          storeName={shopName ?? (currentCollection?.collection?.miqShopifyDomain || "")}
          shop={shopName ?? (currentCollection?.collection?.miqShopifyDomain || "")}
          progressStep={() => {
            setStep(1);
          }}
        />)} 
        {selectedImportMethod === "excel" && (<ExcelImportView/>)}
        </div>
      ),
    },
    {
      title: t('getStarted.well_done'),
      description: t('getStarted.well_done_desc'),
      content: (
        <div className="space-y-6">
          <div className="flex justify-center">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
          <p className="text-center text-gray-600">
            {t('getStarted.analyze_message')}
          </p>
          <Button
            className="w-full"
            onClick={async () => {
              await importShopify();
              navigate("/admin/dashboard");
            }}
          >
            {t('getStarted.start_importing')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex items-center justify-center">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-4">
          <div className="flex justify-between items-center">
            <Progress
              value={((step + 1) / steps.length) * 100}
              className="w-24"
            />
            <span className="text-sm text-gray-500">
              {step + 1} {t('getStarted.step_of')} {steps.length}
            </span>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold tracking-tight">
              {steps[step].title}
            </h2>
            <p className="text-sm text-gray-500">{steps[step].description}</p>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">{steps[step].content}</CardContent>
      </Card>
    </div>
  );
};

export default GetStartedView;
